import type { MRT_ColumnFiltersState, MRT_FilterFn, MRT_Row, MRT_Column } from "material-react-table";
import type { Row } from "@tanstack/table-core";
import type { Job, Company, Branch, Region } from "../../../models";
import type { IAirtableAttachment, SavedView, SavedViewState } from "../../../utilities/types";
import { ViewGroup } from "../../../utilities/types";
import { API } from "../../../api";
import type { Filter } from "../../../api/common";
import type { DashboardURLState, FileMenuItem } from "./JobsDashboardTable.types";
import {
	DEFAULT_FILTER_FNS,
	DEFAULT_FILTERS,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_SORT,
	DEFAULT_GROUPING,
	DEFAULT_VIEW_NAME,
	DEFAULT_VIS_STATE,
} from "./JobsDashboardTable.types";

export function isArrayBasedFilter(filterFn: string): boolean {
	return filterFn === "arrIncludesSome" || filterFn === "arrIncludes";
}

export function getFieldNameById<T extends { id: string; name: string }>(
	ids: string[] | undefined,
	mainList: T[],
	fallbackList: T[] = [],
): string {
	if (!ids || !ids.length) {
		return "";
	}
	const id = ids[0];
	let item = mainList.find((el) => el.id === id);
	if (!item) {
		item = fallbackList.find((el) => el.id === id);
	}
	return item ? item.name : "";
}

export function getValuesFromFilters(filters: MRT_ColumnFiltersState, key: string): string[] {
	const filter = filters.find((filter) => filter.id === key);
	if (!filter || !filter.value) {
		return [];
	}
	return Array.isArray(filter.value) ? filter.value : [filter.value.toString()];
}

export function getBranchesFromFilters(filters: MRT_ColumnFiltersState): string[] {
	return getValuesFromFilters(filters, "branch");
}

export function getRegionsFromFilters(filters: MRT_ColumnFiltersState): string[] {
	return getValuesFromFilters(filters, "region");
}

export function buildJobFilter(company: Company, branchNames: string[], regionNames: string[]): Filter<Job> {
	if (branchNames.length === 0 && regionNames.length === 0) {
		return { fields: { companyName: company.name } } as Filter<Job>;
	} else {
		const filters = [];
		if (regionNames.length > 0) {
			filters.push({
				type: "OR",
				fields: regionNames.map((name) => ({ regionIds: [name] })),
			} as Filter<Job>);
		}
		if (branchNames.length > 0) {
			filters.push({
				type: "OR",
				fields: branchNames.map((name) => ({ branchIds: [name] })),
			} as Filter<Job>);
		}
		return {
			type: "AND",
			fields: [
				{ companyName: company.name },
				{
					type: "OR",
					fields: filters,
				} as Filter<Job>,
			],
		} as Filter<Job>;
	}
}

export function getMarkReadyText(unreadySelectedJobs: Job[]): string {
	switch (unreadySelectedJobs.length) {
		case 0:
			return "Mark Jobs Ready";
		case 1:
			return "Mark 1 Job Ready";
		default:
			return `Mark ${unreadySelectedJobs.length} Jobs Ready`;
	}
}

export const booleanFilterFn: MRT_FilterFn<any> = (row, columnId, filterValue) => {
	const columnValue = row.getValue<string>(columnId).toLowerCase();
	const queryElements = filterValue.match(/\w+/g);
	const terms = queryElements
		?.filter((element: string) => element !== "AND" && element !== "OR")
		.map((element: string) => element.toLowerCase());
	const operators = filterValue.match(/(AND|OR)/g);

	if (!terms || terms.length === 0) {
		return true;
	}

	const evaluateAnds = (terms: string[], columnValue: string) => {
		return terms.reduce((acc, term) => acc && columnValue.includes(term), true);
	};

	let result = false;
	let currentTerms: string[] = [];
	let currentOperator = "OR";

	for (let i = 0; i < terms.length; i++) {
		currentTerms.push(terms[i]);
		const nextOperator = operators?.[i] || "OR";

		if (nextOperator === "OR" || i === terms.length - 1) {
			const andResult = evaluateAnds(currentTerms, columnValue);
			if (currentOperator === "AND") {
				result = result && andResult;
			} else {
				result = result || andResult;
			}
			currentTerms = [];
			currentOperator = nextOperator;
		}
	}

	return result || false;
};

export function generateCSV(columns: MRT_Column<Job, unknown>[], rows: Row<Job>[]): string {
	const headers = columns.map((col) => col.columnDef.header?.toString() || col.id);
	const csvRows = [headers.join(",")];
	for (const row of rows) {
		const rowData = [];

		for (const column of columns) {
			const value = row.getValue(column.id);

			if (value === null || value === undefined) {
				rowData.push("");
			} else if (typeof value === "boolean") {
				rowData.push(value ? "true" : "false");
			} else if (value instanceof Date) {
				rowData.push(value.toISOString().split("T")[0]);
			} else if (Array.isArray(value)) {
				const escapedValue = value.join(", ").replace(/"/g, '""');
				rowData.push(`"${escapedValue}"`);
			} else {
				const stringValue = String(value);
				if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
					rowData.push(`"${stringValue.replace(/"/g, '""')}"`);
				} else {
					rowData.push(stringValue);
				}
			}
		}

		csvRows.push(rowData.join(","));
	}
	return csvRows.join("\n");
}

export function getDownloadItemName(actionMenuRow: MRT_Row<Job> | null, selectedJobs: Job[]): string {
	if (!actionMenuRow) {
		if (selectedJobs.length === 0) {
			return "All Jobs";
		} else {
			return "Selected Jobs";
		}
	} else if (actionMenuRow.getIsGrouped()) {
		return actionMenuRow.groupingValue as string;
	} else {
		return actionMenuRow.original.fieldName;
	}
}

export function getDownloadRowName(actionMenuRow: MRT_Row<Job> | null, selectedJobs: Job[]): string {
	if (!actionMenuRow) {
		if (selectedJobs.length === 0) {
			return "All Jobs";
		} else {
			return "Selected Jobs";
		}
	} else if (actionMenuRow.getIsGrouped()) {
		return actionMenuRow.groupingValue as string;
	} else {
		return actionMenuRow.original.fieldName;
	}
}

export function getJobIdFromRow(actionMenuRow: MRT_Row<Job> | null, _selectedJobs: Job[]): string {
	if (!actionMenuRow) {
		return "";
	} else if (actionMenuRow.getIsGrouped()) {
		return "";
	} else {
		return actionMenuRow.original.id;
	}
}

export function isGeoFileMenuItem(item: FileMenuItem): boolean {
	return item.property.endsWith("SHP");
}

export function getJobAttachmentsForMenuItem(
	fileMenuItem: FileMenuItem,
	actionMenuRow: MRT_Row<Job> | null,
	selectedJobs: Job[],
	allJobs: Job[],
): { job: Job; attachments: IAirtableAttachment[] }[] {
	const result: { job: Job; attachments: IAirtableAttachment[] }[] = [];

	function addFromJob(job: Job | undefined) {
		if (job) {
			const files = job.attachments[fileMenuItem.property];
			if (files?.length) result.push({ job, attachments: files });
		}
	}

	if (!actionMenuRow) {
		const jobs = selectedJobs.length === 0 ? allJobs : selectedJobs;
		for (const job of jobs) addFromJob(job);
	} else if (actionMenuRow.getIsGrouped()) {
		const addRecursive = (subRows: MRT_Row<Job>[]) => {
			subRows.forEach((subRow) => {
				if (subRow.subRows?.length) addRecursive(subRow.subRows);
				else addFromJob(subRow.original);
			});
		};
		addRecursive(actionMenuRow?.subRows ?? []);
	} else {
		addFromJob(actionMenuRow?.original);
	}

	return result;
}

export function getFilesForMenuItem(
	fileMenuItem: FileMenuItem,
	actionMenuRow: MRT_Row<Job> | null,
	selectedJobs: Job[],
	allJobs: Job[],
): IAirtableAttachment[] {
	const files: IAirtableAttachment[] = [];

	function getFilesFromJob(job: Job | undefined) {
		if (job) {
			const file = job.attachments[fileMenuItem.property];
			if (file) files.push(...file);
		}
	}

	if (!actionMenuRow) {
		if (selectedJobs.length === 0) {
			for (let i = 0; i < (allJobs.length ?? 0); i++) {
				getFilesFromJob(allJobs[i]);
			}
		} else {
			for (let i = 0; i < (selectedJobs.length ?? 0); i++) {
				getFilesFromJob(selectedJobs[i]!);
			}
		}
	} else if (actionMenuRow.getIsGrouped()) {
		const getFilesRecursive = (subRows: MRT_Row<Job>[]) => {
			subRows.forEach((subRow) => {
				if (subRow.subRows?.length) {
					getFilesRecursive(subRow.subRows);
				} else {
					getFilesFromJob(subRow.original);
				}
			});
		};
		getFilesRecursive(actionMenuRow?.subRows ?? []);
	} else {
		getFilesFromJob(actionMenuRow?.original);
	}

	return files;
}

export function getAllFilesForMenuItems(
	fileMenuList: FileMenuItem[],
	actionMenuRow: MRT_Row<Job> | null,
	selectedJobs: Job[],
	allJobs: Job[],
): IAirtableAttachment[] {
	const allFiles: IAirtableAttachment[] = [];
	fileMenuList.forEach((fileMenuItem) => {
		const files = getFilesForMenuItem(fileMenuItem, actionMenuRow, selectedJobs, allJobs);
		allFiles.push(...files);
	});
	return allFiles;
}

export interface MultiSelectOptions {
	jobStatusList: string[];
	testPackageList: string[];
	submitterList: string[];
	seasonList: string[];
	branchList: string[];
	regionList: string[];
}

export function getMultiSelectOptions(jobs: Job[], branches: Branch[], regions: Region[]): MultiSelectOptions {
	return {
		jobStatusList: getSelectListValues(jobs, "status", branches, regions),
		testPackageList: getSelectListValues(jobs, "testPackage", branches, regions),
		submitterList: getSelectListValues(jobs, "submitterName", branches, regions),
		seasonList: getSelectListValues(jobs, "season", branches, regions),
		branchList: getSelectListValues(jobs, "branchIds", branches, regions),
		regionList: getSelectListValues(jobs, "regionIds", branches, regions),
	};
}

export function getSelectListValues(
	jobs: Job[],
	fieldName: keyof Job,
	branches: Branch[],
	regions: Region[],
): string[] {
	const all = jobs
		.map((job) => {
			if (fieldName === "branchIds") {
				return getFieldNameById(job.branchIds, branches);
			} else if (fieldName === "regionIds") {
				return getFieldNameById(job.regionIds, regions);
			} else {
				return job[fieldName];
			}
		})
		.filter((value, index, self) => self.indexOf(value) === index)
		.flat();
	return [...new Set(all)].filter((value): value is string => value !== undefined).sort();
}

export function persistViews(
	newView: SavedView,
	newViews: SavedView[],
	userContextId: string | undefined,
	userContextEmail: string | undefined,
	selectedCompany: Company | null,
): void {
	if (newView.group === ViewGroup.User) {
		const userViews = newViews.filter((view) => view.group === ViewGroup.User);
		if (userContextId && userContextEmail) {
			API.Users.updatePortalSettings(userContextEmail, { views: userViews });
		}
	} else if (newView.group === ViewGroup.Company) {
		const companyViews = newViews.filter((view) => view.group === ViewGroup.Company);
		if (selectedCompany) {
			if (!selectedCompany.portalSettings) {
				// @ts-ignore
				selectedCompany.portalSettings = { views: [] };
			}
			selectedCompany.portalSettings.views = companyViews;
			API.Data.Companies.update.portalSettings(selectedCompany.id, selectedCompany.portalSettings);
		}
	}
}

// #region SavedView Helpers

export function generateViewId(): string {
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	// Fallback for environments without crypto.randomUUID
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export function dashboardStateToSavedViewState(state: DashboardURLState): SavedViewState {
	return {
		sorting: state.sorting,
		grouping: state.grouping,
		columnOrder: state.columnOrder,
		columnVisibility: state.columnVisibility,
		columnFilterFunctions: state.columnFilterFunctions as Record<string, string>,
		columnFilterValues: state.columnFilterValues,
	};
}

export function savedViewStateToDashboardState(state: SavedViewState): DashboardURLState | null {
	if (!state) {
		return null;
	}

	return {
		sorting: state?.sorting ?? DEFAULT_COLUMN_SORT,
		grouping: state?.grouping ?? DEFAULT_GROUPING,
		columnOrder: state?.columnOrder ?? DEFAULT_COLUMN_ORDER,
		columnVisibility: state?.columnVisibility ?? DEFAULT_VIS_STATE,
		columnFilterFunctions: state?.columnFilterFunctions ?? DEFAULT_FILTER_FNS,
		columnFilterValues: state?.columnFilterValues ?? DEFAULT_FILTERS,
	};
}

export function findViewById(views: SavedView[], id: string): SavedView | null {
	return views.find((view) => view.id === id) ?? null;
}

export function buildShareUrl(view: SavedView, companyId?: string): string {
	const baseUrl = window.location.origin + window.location.pathname + window.location.hash.split("?")[0];
	const params = new URLSearchParams();
	params.set("v", view.id);
	if (companyId) {
		params.set("company", companyId);
	}
	return `${baseUrl}?${params.toString()}`;
}

export function createDefaultSavedViews(branchCount: number): SavedView[] {
	const baseGrouping = ["season", "branch", "grower"];
	const grouping = branchCount <= 1 ? baseGrouping.filter((g) => g !== "branch") : baseGrouping;
	const now = new Date().toISOString();

	const changesVisibility = {
		grower: true,
		field: true,
		farm: true,
		"job status": true,
		"boundary acres": false,
		"field ready date": false,
		"sample date": false,
		"creation date": false,
		"drop/ship date": false,
		"test package": false,
		edits: false,
		"files last edited?": false,
		submitter: false,
		"approved billing?": false,
		boundary: false,
		"lab sent results": false,
		branch: false,
		region: false,
		"job flags": false,
		"points change?": true,
		"boundary change?": true,
		"boundary approved?": true,
		"test job?": false,
	};

	return [
		{
			id: generateViewId(),
			name: DEFAULT_VIEW_NAME,
			group: ViewGroup.Default,
			state: {
				sorting: DEFAULT_COLUMN_SORT,
				grouping,
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: { grower: false, branch: false, region: false, season: false, "test job?": false },
				columnFilterFunctions: { ...DEFAULT_FILTER_FNS },
				columnFilterValues: DEFAULT_FILTERS,
			},
			createdAt: now,
			updatedAt: now,
		},
		{
			id: generateViewId(),
			name: "Group by Status",
			group: ViewGroup.Default,
			state: {
				sorting: DEFAULT_COLUMN_SORT,
				grouping: ["job status"],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: { "job status": false, "test job?": false },
				columnFilterFunctions: { ...DEFAULT_FILTER_FNS },
				columnFilterValues: DEFAULT_FILTERS,
			},
			createdAt: now,
			updatedAt: now,
		},
		{
			id: generateViewId(),
			name: "Unready Jobs",
			group: ViewGroup.Default,
			state: {
				sorting: DEFAULT_COLUMN_SORT,
				grouping: ["grower"],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: changesVisibility,
				columnFilterFunctions: { ...DEFAULT_FILTER_FNS },
				columnFilterValues: [{ id: "job status", value: ["0. Unready"] }],
			},
			createdAt: now,
			updatedAt: now,
		},
		{
			id: generateViewId(),
			name: "Boundary Changes",
			group: ViewGroup.Default,
			state: {
				sorting: DEFAULT_COLUMN_SORT,
				grouping: ["grower"],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: changesVisibility,
				columnFilterFunctions: { ...DEFAULT_FILTER_FNS },
				columnFilterValues: [
					{ id: "boundary change?", value: true },
					{ id: "boundary approved?", value: true },
				],
			},
			createdAt: now,
			updatedAt: now,
		},
		{
			id: generateViewId(),
			name: "Points Changes",
			group: ViewGroup.Default,
			state: {
				sorting: DEFAULT_COLUMN_SORT,
				grouping: ["grower"],
				columnOrder: DEFAULT_COLUMN_ORDER,
				columnVisibility: changesVisibility,
				columnFilterFunctions: { ...DEFAULT_FILTER_FNS },
				columnFilterValues: [{ id: "points change?", value: true }],
			},
			createdAt: now,
			updatedAt: now,
		},
	];
}

// #endregion
